<template lang="pug">
    include ../../icon/basic

    div(
        class="_pickf",
        :style="{ width, height }",
        :class="{ _hov: this.hovered }",
        @dragenter.prevent="_dragenter",
        @dragleave.prevent="_dragleave",
        @dragover.prevent="",
        @drop.prevent="_drop")
        input(
            class="_pickfi",
            type="file",
            aria-hidden="true",
            :id="id",
            :name="name",
            :multiple="maxfiles != 1",
            :accept="_accept",
            :capture="capture",
            :inert="inert",
            @change="_change",
            v-on="__on",
            ref="_in")

        div(v-show="!ivalue || !ivalue.length", class="_pickfp")
            +icon-basic-upload_2('ios')
            div
                | {{_ph[0]}}
                a(v-if="_ph[2]", ref="proxy", role="button", v-text="_ph[1]", v-on="__fwd")
                | {{_ph[2] || _ph[1]}}
            div(v-if="maxfiles || maxsize || maxtotal", class="_pickfps fs8 fg50")
                span(v-if="maxfiles", class="_pickfpd", v-text="maxfilest")
                span(v-if="maxsize", class="_pickfpd", v-text="maxsizet")
                span(v-if="maxtotal", class="_pickfpd", v-text="maxtotalt")
                span(v-if="accept", class="_pickfpd", v-text="acceptt")

        ul(class="_pickfl")
            li(v-for="(f,i) in ivalue")
                img(v-if="thumb[i]", class="_pickft", :src="thumb[i]", @error="$set(thumb, i, null)")
                div(v-else, class="_pickft", v-text="((f.name.match(/\\.([^.]*)$/)||[])[1]||'?').toLowerCase()")
                div(class="_pickfd")
                    p(class="text-bold") {{f.name}}
                    p(class="fs7 fg50")
                        span(v-num.bytes="f.size")
                        span(v-if="maxfiles && i >= maxfiles",
                            class="fg-error start-lg",
                            v-text="$locale.data.dropMaxFilesErr.replace('{n}',$num.int(maxfiles))")
                        span(v-if="maxsize && f.size > maxsize",
                            class="fg-error start-lg",
                            v-text="$locale.data.dropMaxSizeErr.replace('{n}',$num.bytes(maxsize))")
                        span(v-if="accept && !accept.includes(f.type)",
                            class="fg-error start-lg",
                            v-text="$locale.data.dropAcceptErr")
                ib-button(class="_pickfb link fs5 fg50", @click="_delete(i)")
                    +icon-basic-cancel_filled
            li(v-if="ivalue && ivalue.length && (!maxfiles || ivalue.length < maxfiles)")
                div(class="_pickft bg10-accent fg-accent")
                    +icon-basic-plus-large
                div(class="_pickfd text-bold")
                    a(role="button", @click="__fwdclick", v-text="$locale.data.dropAdd")
</template>

<script>
    import entry from './ib-form-entry.vue';
    import telemetry from '../private/telemetry';
    import $locale from '../../prototype/locale';
    import $num from '../../prototype/num';
    import $string from '../../prototype/string';

    const slice = [].slice;


    /**
     * A file upload component that accepts drag and drop, or browse modes.
     */
    export default {
        name: 'ib-picker-file',

        mixins: [
            entry,
            telemetry
        ],

        props: {
            /**
             * Maximum number of files allowed.
             */
            maxfiles: Number,
            /**
             * Maximum number of bytes per file.
             */
            maxsize: Number,
            /**
             * Maximum number of bytes total.
             */
            maxtotal: Number,
            /**
             * List of acceptable MIME types.
             */
            accept: Array,
            /**
             * If specified, mobile devices will default to live camera rather than storage.
             * Specify `user` for user-facing camera or `environment` for outward facing camera.
             */
            capture: String,
            /**
             * A placeholder string displayed when field content is empty.
             */
            placeholder: String
        },

        data() {
            return {
                thumb: [],
                hovered: null
            };
        },

        computed: {
            _ph() {
                return (this.placeholder || $locale.data.dropFiles).split('**');
            },

            _accept() {
                return this.accept ? this.accept.join(',') : null;
            },

            maxfilest() {
                return $locale.data.dropMaxFiles.replace('{n}', $num.int(this.maxfiles));
            },

            maxsizet() {
                return $locale.data.dropMaxSize.replace('{n}', $num.bytes(this.maxsize));
            },

            maxtotalt() {
                return $locale.data.dropMaxTotal.replace('{n}', $num.bytes(this.maxtotal));
            },

            acceptt() {
                return this.accept ? $locale.data.dropAccept.replace('{t}', this.accept.map(type => (/\/.*?([^.]+)$/.exec(type)||[])[1].toUpperCase()).join(', ')) : null;
            }
        },

        methods: {
            add(files) {
                let value = this.ivalue || [];
                let length = value.length;
                this._set(value.concat(files));
                for (let i = 0; i < files.length; i++) {
                    this.thumb[i + length] = null;
                    if (files[i].type.startsWith('image/')) {
                        const reader = new FileReader();
                        reader.onloadend = () => { this.$set(this.thumb, i + length, reader.result); };
                        reader.readAsDataURL(files[i]);
                    }
                }
                this.validate();
            },

            _change() {
                this.add(slice.call(this.$refs._in.files));
            },

            _dragenter(e) {
                this.hovered = e.target;
            },

            _dragleave(e) {
                if (this.hovered === e.target) {
                    this.hovered = null;
                }
            },

            _drop(e) {
                const files = e.dataTransfer.files;
                if (files) {
                    this.add([...files]);
                }
                this.hovered = null;
            },

            _delete(i) {
                this.ivalue.splice(i, 1);
                this.thumb.splice(i, 1);
                if (!this.ivalue.length) {
                    this.$refs._in.value = '';
                }
                this.validate();
            },

            validate() {
                const ivalue = this.ivalue;
                const accept = this.accept;
                let validity;
                if (this.required && (!ivalue || !ivalue.length)) {
                    validity = $locale.data.selFile;
                } else if (this.maxfiles && ivalue && ivalue.length > this.maxfiles) {
                    validity = $locale.data.dropMaxFilesErr.replace('{n}', $num.int(this.maxfiles));
                } else if (this.maxsize && ivalue && ivalue.some(file => file.size > this.maxsize)) {
                    validity = $locale.data.dropMaxSizeErr.replace('{n}', $num.bytes(this.maxsize));
                } else if (this.maxtotal && ivalue && ivalue.reduce((total,file) => total + file.size, 0) > this.maxtotal) {
                    validity = $locale.data.dropMaxTotalErr.replace('{n}', $num.bytes(this.maxtotal));
                } else if (accept && ivalue && ivalue.some(file => !accept.includes(file.type))) {
                    validity = $locale.data.dropAcceptErr;
                }
                this.validity = validity;
                this.valid = !validity;
                this.__refresherror();
                return !validity;
            },

            getvalidity() {
                return this.validity;
            }
        }
    }
</script>

<style lang="less">
    @import "../../less/globals.less";

    ._pickf {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        position: relative;
        margin: 2px 3px;
        border: 1px dashed @border-field;
        min-width: 160px;
        min-height: 140px;
        &._hov {
            background-color: fade(@fg-accent, 5%);
            border-color: @fg-accent;
            > ._pickfp > svg {
                color: fade(@fg-accent, 70%);
            }
        }
    }
    ._pickfi {
        display: none;
    }
    ._pickfp {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        text-align: center;
        svg {
            width: 64px;
            height: auto;
            max-width: 15%;
            color: fade(@fg, 30%);
        }
    }
    ._pickfpd {
        padding: 0 8px;
        white-space: nowrap;
    }
    ._pickfps {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        margin: 8px 0;
    }
    ._pickfl {
        display: flex;
        flex-direction: column;
        align-items: stretch;
        margin: 0;
        padding: 8px;
        list-style: none;
        > li {
            display: flex;
            align-items: center;
            margin: 0;
            padding: 6px;
            white-space: nowrap;
        }
    }
    ._pickft {
        display: flex;
        align-items: center;
        justify-content: center;
        flex: 0 0 auto;
        width: 48px;
        height: 48px;
        object-fit: cover;
        .font-size(@fs8);
        .shadow-low();
        white-space: nowrap;
        overflow: hidden;
        background-color: mix(@fg-max, @bg, 4%);
        color: mix(@fg-max, @bg, 30%);
    }
    ._pickfd {
        flex: 1 1 auto;
        padding: 0 8px;
        overflow: hidden;
        > p {
            overflow: hidden;
            text-overflow: ellipsis;
        }
    }
    ._pickfb {
        flex: 0 0 auto;
    }
    ._pickff {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
    }
</style>
